import { useEffect, useState } from "react";

/*
 * "Add to Home Screen" nudge.
 *
 * Two worlds. Android/Chrome fires `beforeinstallprompt`, which we capture and
 * replay behind our own button to pop the native install dialog. iOS has no
 * such event and only Safari can install, so there we show plain instructions.
 *
 * Never shown when the app is already running from its icon (standalone), and
 * a dismissal is remembered so it nudges at most once a fortnight.
 */

const DISMISS_KEY = "cp-a2hs-dismissed-at";
const DISMISS_DAYS = 14;

type InstallEvent = Event & { prompt: () => void; userChoice: Promise<unknown> };

export function InstallPrompt() {
  const [deferred, setDeferred] = useState<InstallEvent | null>(null);
  const [ios, setIos] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as unknown as { standalone?: boolean }).standalone === true;
    if (standalone) return; // already installed and running from the icon

    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 86_400_000) return;

    const ua = navigator.userAgent;
    const isIos = /iphone|ipad|ipod/i.test(ua);

    if (isIos) {
      // Only Safari can Add to Home Screen; Chrome/Firefox on iOS cannot.
      const isSafari = /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
      if (isSafari) {
        setIos(true);
        setShow(true);
      }
      return;
    }

    const onPrompt = (event: Event) => {
      event.preventDefault();
      setDeferred(event as InstallEvent);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  }

  async function install() {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    dismiss();
  }

  if (!show) return null;

  return (
    <div className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-md rounded-xl border border-neutral-200 bg-white p-4 shadow-lg dark:border-neutral-700 dark:bg-neutral-900">
      <div className="flex items-start gap-3">
        <span aria-hidden className="mt-0.5 text-xl text-brand-600">◉</span>
        <div className="flex-1 text-sm">
          <p className="font-medium">Add Court Pilot to your home screen</p>
          {ios ? (
            <p className="mt-0.5 text-neutral-500 dark:text-neutral-400">
              Tap the Share button, then <span className="font-medium">Add to Home Screen</span> — it
              opens like an app, no browser needed.
            </p>
          ) : (
            <p className="mt-0.5 text-neutral-500 dark:text-neutral-400">
              Open it like an app, straight from your home screen.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="text-neutral-400 hover:text-neutral-600"
        >
          ✕
        </button>
      </div>

      {!ios && (
        <button
          type="button"
          onClick={install}
          className="mt-3 w-full rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          Install
        </button>
      )}
    </div>
  );
}
